import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend
} from 'recharts';
import DeltaBadge from './DeltaBadge';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload.find(e => e.value != null);
  if (!item) return null;
  const { label, level, date } = item.payload || {};
  return (
    <div style={{
      background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8,
      padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      fontSize: 12,
    }}>
      <div style={{ fontWeight: 600, color: '#262626', marginBottom: 4 }}>{label}</div>
      {payload.filter(e => e.value != null).map((entry, i) => (
        <div key={i} style={{ color: entry.color }}>
          NPS: {entry.value?.toFixed(1)}
        </div>
      ))}
      {level && <div style={{ color: '#595959' }}>Level: {level}</div>}
      {date && <div style={{ color: '#8c8c8c', marginTop: 2 }}>{date}</div>}
    </div>
  );
};

const ProgressionGraph = ({ data = [], title = 'Improvement', currentAssessmentId = null, height = 250, delta = null, showPractice = true }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{
        textAlign: 'center', padding: '28px 20px', color: '#8c8c8c', fontSize: 13,
        background: '#fafafa', borderRadius: 10, border: '1px dashed #e8e8e8',
      }}>
        No progression data available
      </div>
    );
  }

  const sorted = [...data].sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt));

  const nonPractice = sorted.filter(d => !d.isPractice);
  const practice = sorted.filter(d => d.isPractice);

  // Merge all diagnosis entries into a single "Diag." point (use latest NPS)
  const diagEntries = nonPractice.filter(d => d.isDiagnosis);
  const nonDiagEntries = nonPractice.filter(d => !d.isDiagnosis);

  const merged = [];
  if (diagEntries.length > 0) {
    const lastDiag = diagEntries[diagEntries.length - 1];
    merged.push({
      label: 'Diag.',
      displayLabel: 'Diag.',
      assessmentNps: lastDiag.nps,
      practiceNps: null,
      assessmentId: lastDiag.id || null,
      isPractice: false,
      level: lastDiag.cefr || lastDiag.level || null,
      date: formatDate(lastDiag.submittedAt),
    });
  }
  let assessCount = 0;
  nonDiagEntries.forEach(d => {
    assessCount++;
    merged.push({
      label: `Assmt. ${assessCount}`,
      displayLabel: `Assmt. ${assessCount}`,
      assessmentNps: d.nps,
      practiceNps: null,
      assessmentId: d.id || null,
      isPractice: false,
      level: d.cefr || d.level || null,
      date: formatDate(d.submittedAt),
    });
  });

  if (showPractice) {
    const assessmentPoints = merged.filter(m => m.label !== 'Diag.');
    practice.forEach((p, i) => {
      if (i < assessmentPoints.length) {
        assessmentPoints[i].practiceNps = p.nps;
      }
    });
  }

  const hasPractice = showPractice && merged.some(d => d.practiceNps != null);

  const currentIdx = currentAssessmentId
    ? merged.findIndex(d => d.assessmentId === currentAssessmentId)
    : -1;

  const assessmentColor = '#3249D7';
  const practiceColor = '#F59E0B';

  const gradId = `grad-${(title || '').replace(/\s/g, '')}`;
  const gradPracticeId = `gradP-${(title || '').replace(/\s/g, '')}`;

  return (
    <div style={{
      background: '#fff', borderRadius: 10, padding: '16px 16px 8px',
      border: '1px solid #f0f0f0',
    }}>
      {title && (
        <div style={{
          fontSize: 13, fontWeight: 600, color: '#262626', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            width: 3, height: 14, borderRadius: 2, background: assessmentColor,
            display: 'inline-block',
          }} />
          {title}
          {delta != null && <DeltaBadge delta={delta} />}
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={merged} margin={{ top: 5, right: 16, left: -10, bottom: 5 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={assessmentColor} stopOpacity={0.15} />
              <stop offset="100%" stopColor={assessmentColor} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id={gradPracticeId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={practiceColor} stopOpacity={0.1} />
              <stop offset="100%" stopColor={practiceColor} stopOpacity={0.01} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: '#8c8c8c' }}
            axisLine={{ stroke: '#f0f0f0' }}
            tickLine={false}
            angle={-28}
            textAnchor="end"
            height={52}
            interval={0}
            tickMargin={6}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tick={{ fontSize: 11, fill: '#8c8c8c' }}
            axisLine={false}
            tickLine={false}
            width={30}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
            iconType="circle"
            iconSize={8}
          />
          <Area
            type="monotone" dataKey="assessmentNps"
            stroke={assessmentColor} strokeWidth={2}
            fill={`url(#${gradId})`}
            name="Assessment" connectNulls
            dot={{ r: 4, fill: assessmentColor, stroke: '#fff', strokeWidth: 2 }}
            activeDot={{ r: 6, stroke: assessmentColor, strokeWidth: 2, fill: '#fff' }}
          />
          {hasPractice && (
            <Area
              type="monotone" dataKey="practiceNps"
              stroke={practiceColor} strokeWidth={2} strokeDasharray="5 5"
              fill={`url(#${gradPracticeId})`}
              name="Practice" connectNulls
              dot={{ r: 3, fill: '#fff', stroke: practiceColor, strokeWidth: 2 }}
              activeDot={{ r: 5, stroke: practiceColor, strokeWidth: 2, fill: '#fff' }}
            />
          )}
          {currentIdx >= 0 && (
            <ReferenceLine
              x={merged[currentIdx]?.label}
              stroke={assessmentColor} strokeDasharray="4 4" strokeWidth={1.5}
              label={{ value: 'Current', position: 'top', fill: assessmentColor, fontSize: 10, fontWeight: 600 }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ProgressionGraph;
